import type { ReactNode } from "react"
import { useChartTheme } from "@/lib/chart/ChartThemeContext"
import { cn } from "@/lib/cn"
import {
  CHART_INTERVALS,
  CHART_RANGES,
  type ChartInterval,
  type ChartRange,
} from "@/lib/chart/timeframes"

interface Props {
  range: ChartRange
  onRangeChange: (range: ChartRange) => void
  /** The ranges to offer, for a series too short for all of them; every range by default. */
  ranges?: readonly ChartRange[]
  interval?: ChartInterval
  onIntervalChange?: (interval: ChartInterval) => void
}

export function ChartTimeframeControls({
  range,
  onRangeChange,
  interval,
  onIntervalChange,
  ranges,
}: Props) {
  return (
    <>
      {interval && onIntervalChange && (
        <SegmentedGroup ariaLabel="Chart interval">
          {CHART_INTERVALS.map((item) => (
            <SegmentButton
              key={item.id}
              active={interval === item.id}
              onClick={() => onIntervalChange(item.id)}
            >
              {item.label}
            </SegmentButton>
          ))}
        </SegmentedGroup>
      )}
      <SegmentedGroup ariaLabel="Chart range">
        {CHART_RANGES.filter((item) => !ranges || ranges.includes(item.id)).map((item) => (
          <SegmentButton
            key={item.id}
            active={range === item.id}
            onClick={() => onRangeChange(item.id)}
          >
            {item.label}
          </SegmentButton>
        ))}
      </SegmentedGroup>
    </>
  )
}

function SegmentedGroup({
  ariaLabel,
  children,
}: {
  ariaLabel: string
  children: ReactNode
}) {
  const theme = useChartTheme()
  return (
    <div className={cn("flex rounded-lg p-0.5", theme.classes.segmentGroup)} role="group" aria-label={ariaLabel}>
      {children}
    </div>
  )
}

function SegmentButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  const theme = useChartTheme()
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded-md px-3 py-1 text-[11px] font-medium transition",
        active ? theme.classes.segmentOn : theme.classes.segmentOff,
      )}
    >
      {children}
    </button>
  )
}
